import axios from "axios";
import { useAuthStore } from "../stores/authStore";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// ── Request interceptor ──
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.headers["x-request-id"] = crypto.randomUUID();

  // Attach an idempotency key to every state-mutating request BEFORE the
  // request is sent. The SW reads and preserves this header when it has to
  // enqueue a failed request for background retry, ensuring that a server
  // which processed the original request but lost the response does not
  // execute the mutation a second time.
  const mutatingMethods = ["post", "put", "patch", "delete"];
  if (
    mutatingMethods.includes((config.method || "").toLowerCase()) &&
    !config.headers["x-idempotency-key"]
  ) {
    config.headers["x-idempotency-key"] = crypto.randomUUID();
  }

  return config;
});

// ── Response interceptor (refresh + retry) ──
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    const body = response.data;
    // Unwrap ApiResponse envelope: { success, data, message, timestamp } → data
    if (
      body &&
      typeof body === "object" &&
      "success" in body &&
      "data" in body
    ) {
      let inner = body.data;
      // Auto-unwrap single-key wrapper objects like { categories: [...] } → [...]
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        const keys = Object.keys(inner);
        if (keys.length === 1) {
          inner = inner[keys[0]];
        }
      }
      response.data = inner;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // No response at all → network error
    if (!error.response) {
      return Promise.reject(error);
    }

    // 401 — attempt token refresh (but not for auth endpoints)
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh")
    ) {
      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { setAccessToken, logout } = useAuthStore.getState();

        // Always read the refresh token from localStorage directly — Zustand
        // caches the value at init time and won't see updates made by other
        // tabs.  Reading localStorage here ensures we always send the most
        // recent token even after another tab has already rotated it.
        const REFRESH_KEY = "dm_refresh_token";
        const refreshToken = localStorage.getItem(REFRESH_KEY);

        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const newAccess = data.data.accessToken;
        const newRefresh = data.data.refreshToken;

        setAccessToken(newAccess);
        useAuthStore.getState().setRefreshToken(newRefresh);

        processQueue(null, newAccess);

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Before logging out, check whether another tab already refreshed
        // successfully and wrote a newer token to localStorage.  If so, use
        // that token for the queued requests instead of logging out.
        const REFRESH_KEY = "dm_refresh_token";
        const latestToken = localStorage.getItem(REFRESH_KEY);
        const zustandToken = useAuthStore.getState().refreshToken;

        if (latestToken && latestToken !== zustandToken) {
          // Another tab rotated the token — retry the refresh with the new one
          try {
            const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken: latestToken,
            });
            const newAccess = data.data.accessToken;
            const newRefresh = data.data.refreshToken;
            useAuthStore.getState().setAccessToken(newAccess);
            useAuthStore.getState().setRefreshToken(newRefresh);
            processQueue(null, newAccess);
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return api(originalRequest);
          } catch {
            // Second attempt also failed — give up
          }
        }

        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;

/**
 * Helper to extract error message from API error responses.
 */
export function getErrorMessage(error) {
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message === "Network Error")
    return "Network error — please check your connection";
  if (error.code === "ECONNABORTED")
    return "Request timed out — please try again";
  return error.message || "Something went wrong";
}
